// Soft-keyboard visibility seam (MobileShell "keyboard-up hides the tab bar"). The native Keyboard
// plugin lives in the wrapper's SPM plugin set (`Keyboard: { resize: 'body' }`) — accessed via
// `registerPlugin` so @cntrl/native needs no extra npm dep. No-op on web (a browser has no soft keyboard the
// tab bar must yield to), mirroring lifecycle/push glue.
import { Capacitor, registerPlugin } from "@capacitor/core";
import type { PluginListenerHandle } from "@capacitor/core";

interface KeyboardPlugin {
  addListener(
    event: "keyboardWillShow" | "keyboardWillHide",
    listener: (info?: { keyboardHeight?: number }) => void,
  ): Promise<PluginListenerHandle>;
}

const Keyboard = registerPlugin<KeyboardPlugin>("Keyboard");

/**
 * Invoke <paramref name="callback"/> with <c>true</c> when the soft keyboard is about to show and <c>false</c>
 * when it is about to hide (the MobileShell hides its bottom tab bar while the keyboard is up so a
 * text field is never occluded). No-op on web. Returns a SYNCHRONOUS unsubscribe usable directly in a React
 * effect cleanup; it handles the race where the effect is torn down before the async `addListener` resolves
 * (the `onNativeResume` pattern).
 */
export function onKeyboardToggle(callback: (visible: boolean, height: number) => void): () => void {
  if (!Capacitor.isNativePlatform()) return () => {};

  const handles: PluginListenerHandle[] = [];
  let removed = false;

  const add = (event: "keyboardWillShow" | "keyboardWillHide", visible: boolean) => {
    void Keyboard.addListener(event, (info) => callback(visible, visible ? info?.keyboardHeight ?? 0 : 0)).then((h) => {
      if (removed) void h.remove(); // unsubscribed before the listener finished registering
      else handles.push(h);
    });
  };

  add("keyboardWillShow", true);
  add("keyboardWillHide", false);

  return () => {
    removed = true;
    while (handles.length) void handles.pop()?.remove();
  };
}

/** The CSS custom property the soft keyboard's height is published under (on `<html>`). */
export const KEYBOARD_INSET_VAR = "--keyboard-inset";

/**
 * Publish the soft keyboard's height as `--keyboard-inset` on `<html>` (ART-251) — `0px` while it is down.
 *
 * The wrapper's `Keyboard: { resize: "body" }` shrinks `<body>`, but `100dvh` and `position: fixed` are measured
 * against the VIEWPORT, which the keyboard does not shrink — so the mobile shell (sized `100dvh`) and every fixed
 * overlay (sheets, modals) kept their full height with the keyboard drawn over their bottom: a chat composer you
 * could not see while typing, a task form you could not scroll to. Each of those subtracts this variable instead.
 * Call ONCE at boot; a no-op on web, where a browser resizes its own visual viewport.
 */
export function trackKeyboardInset(): () => void {
  const root = typeof document !== "undefined" ? document.documentElement : null;
  if (!root) return () => {};
  root.style.setProperty(KEYBOARD_INSET_VAR, "0px");
  return onKeyboardToggle((visible, height) => {
    root.style.setProperty(KEYBOARD_INSET_VAR, visible ? `${Math.max(0, Math.round(height))}px` : "0px");
  });
}
